import type { components } from '@connected/api-client/api.generated';

import { cn } from '@/lib/utils';

import { useCompletionReportPatch, useScope10InspectionPatch } from '../hooks/use-completion-mutations';

type TableRow = NonNullable<NonNullable<NonNullable<NonNullable<components['schemas']['EditElectricalProjectInspectionConclusionQueryResult']['Inspection']>['Scope10Inspection']>['Table']>['Rows']>[number];
type TableCell = NonNullable<TableRow['Cells']>[number];

type Scope10InspectionTableProps = {
    tableData: NonNullable<components['schemas']['EditElectricalProjectInspectionConclusionQueryResult']['Inspection']>['Scope10Inspection'];
    projectVersionId: string;
    onCellValueChange: (value: number | null) => void;
};

export function Scope10InspectionTable({
    tableData,
    projectVersionId,
    onCellValueChange,
}: Scope10InspectionTableProps) {
    // Hook for patching Scope 10 table cell selection
    const tablePatcher = useScope10InspectionPatch({ projectVersionId });
    const reportPatcher = useCompletionReportPatch({ projectVersionId, inspectionKind: 'Scope10' });

    // Function to get horizontal alignment
    function getHorizontalAlignment(cell: TableCell) {
        return !cell.HorizontalAlignment
            ? 'center' as const
            : cell.HorizontalAlignment.toLowerCase() as 'left' | 'center' | 'right';
    }

    // Extract the cell click handler function
    async function handleCellClick(
        cell: TableCell,
        rowIndex: number,
        cellIndex: number,
    ) {
        if (cell.Selectable !== true) {
            return;
        }

        const updatedCells = [...(tableData?.Table?.Rows ?? [])];

        const currentlySelectedCell = updatedCells
            .flatMap(row => row.Cells ?? [])
            .find(cell => cell.Selected);

        if (currentlySelectedCell && currentlySelectedCell.CellId !== cell.CellId) {
            currentlySelectedCell.Selected = false;
        }

        const isSelect = !(currentlySelectedCell?.CellId === cell.CellId && cell.Selected);

        const cellToUpdate = updatedCells[rowIndex]?.Cells?.[cellIndex];

        if (cellToUpdate) {
            cellToUpdate.Selected = !cell.Selected;
        }

        // Determine if action is a select or deselect
        const selectedValue = cell.Selected ? cell.CellId : null;
        const nextInspectionValue = cell.Selected ? cell.Value! : null;

        if (isSelect) {
            await tablePatcher.patchField('Scope10InspectionIntervalTableCellId')({ value: selectedValue });
            await reportPatcher.patchField('NextInspectionInYearCount')({ value: nextInspectionValue });

            onCellValueChange(nextInspectionValue);
        }
        else {
            // TODO: Check if this is correct
            await tablePatcher.patchField('Scope10InspectionIntervalTableCellId')({ value: null });
            await reportPatcher.patchField('NextInspectionInYearCount')({ value: null });

            onCellValueChange(null);
        }
    }

    return (
        <div className="overflow-auto rounded-md border-2 xl:max-w-[900px]">
            <table className="w-full border-collapse">
                {tableData?.Table?.Rows?.map(
                    (row, rowIndex) => {
                        const rowKey = `${rowIndex}-${row.RowNumber}`;
                        return (
                            <tr key={rowKey}>
                                {row.Cells?.map(
                                    (cell, cellIndex) => {
                                        const horizontalAlignment = getHorizontalAlignment(cell);
                                        const cellKey = `${rowKey}-${cellIndex}-${cell.CellId}`;

                                        return (
                                            <td
                                                key={cellKey}
                                                rowSpan={cell.RowSpan ?? undefined}
                                                colSpan={cell.ColSpan ?? undefined}
                                                style={{
                                                    fontWeight: cell.Bold ? 'bold' : 'normal',
                                                    textAlign: horizontalAlignment,
                                                    verticalAlign: cell.VerticalAlignment?.toLowerCase() ?? undefined,
                                                }}
                                                className={cn('p-1 transition duration-100', {
                                                    'cursor-pointer select-none hover:bg-background-primary-hover hover:outline hover:outline-primary/30': !!cell.Selectable,
                                                    'outline-solid border-primary outline outline-primary hover:outline-primary ': !!cell.Selected,
                                                    'border-collapse border-2': !cell.Selected,
                                                })}
                                                onClick={async () => handleCellClick(cell, rowIndex, cellIndex)}
                                            >
                                                {cell.Value == null ? cell.Text : cell.Value}
                                            </td>
                                        );
                                    },
                                )}
                            </tr>
                        );
                    },
                ) ?? null}
            </table>
        </div>
    );
}
